import type { MatNode } from '../store/seedanceTypes';
import { getVirtualPortrait } from '../api/virtualPortrait';
import { extractApiMessage, isAbortLike } from './seedanceRequestUtils';
import { SEEDANCE_STORAGE_KEYS, getLocalStorageValue } from './seedanceStorage';

export interface MissingWorkspaceAsset {
  nodeId: string;
  name: string;
  assetId: string;
}

/** 上游明确表示素材 ID 不存在 / 不可用（不是网络抖动）。 */
export const isUpstreamMissingAssetError = (error: unknown): boolean => {
  if (isAbortLike(error)) return false;
  const msg = (
    extractApiMessage(error) ||
    (error instanceof Error ? error.message : String(error ?? ''))
  ).toLowerCase();
  if (!msg.trim()) return false;
  if (msg.includes('invalid_asset')) return true;
  if (msg.includes('notfound')) return true;
  if (msg.includes('not found')) return true;
  if (msg.includes('does not exist')) return true;
  if (msg.includes('不存在')) return true;
  if (msg.includes('找不到')) return true;
  return false;
};

export const disconnectMissingAssetNodes = (
  nodes: MatNode[],
  missingAssetIds: readonly string[],
): MatNode[] => {
  const missing: Record<string, true> = {};
  for (let i = 0; i < missingAssetIds.length; i += 1) {
    const id = missingAssetIds[i].trim();
    if (id) missing[id] = true;
  }
  if (Object.keys(missing).length === 0) return nodes;
  return nodes.map((node) => {
    const sid = (node.sourceAssetId || '').trim();
    if (!sid || !missing[sid] || !node.connected) return node;
    return { ...node, connected: false };
  });
};

export const formatMissingAssetSubmitMessage = (missing: MissingWorkspaceAsset[]): string => {
  const seen: Record<string, true> = {};
  const names: string[] = [];
  for (let i = 0; i < missing.length; i += 1) {
    const name = missing[i].name.trim() || missing[i].assetId;
    if (seen[name]) continue;
    seen[name] = true;
    names.push(name);
  }
  const shown = names.slice(0, 4).join('、');
  const extra = names.length > 4 ? ` 等 ${names.length} 个` : '';
  return `素材「${shown}」${extra}在上游已失效，已取消激活。请从素材库重新选择这些素材后再生成。`;
};

/**
 * 对已激活槽位做 GetAsset 探活。只把明确 NotFound / invalid_asset 视为失效；
 * 网络错误不列入，避免误伤。
 */
export async function probeMissingConnectedAssetIds(
  nodes: MatNode[],
  options: { signal?: AbortSignal; onDebug?: (message: string, isError?: boolean) => void } = {},
): Promise<MissingWorkspaceAsset[]> {
  const { signal, onDebug } = options;
  const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
  const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
  if (!token || !userId) return [];

  const connected = nodes.filter((node) => {
    if (!node.connected) return false;
    if (node.type !== 'image' && node.type !== 'video' && node.type !== 'audio') return false;
    return Boolean((node.sourceAssetId || '').trim());
  });
  if (!connected.length) return [];

  const byAssetId: Record<string, MatNode[]> = {};
  for (let i = 0; i < connected.length; i += 1) {
    const node = connected[i];
    const assetId = (node.sourceAssetId || '').trim();
    const list = byAssetId[assetId] || [];
    list.push(node);
    byAssetId[assetId] = list;
  }

  const assetIds = Object.keys(byAssetId);
  onDebug?.(`工作台：校验 ${assetIds.length} 个素材 ID 是否仍有效…`);
  const missingIds: string[] = [];

  await Promise.all(
    assetIds.map(async (assetId) => {
      if (signal?.aborted) return;
      try {
        await getVirtualPortrait(assetId, {
          signal,
          systemToken: token,
          userId,
        });
      } catch (error) {
        if (isAbortLike(error) || signal?.aborted) return;
        if (isUpstreamMissingAssetError(error)) {
          missingIds.push(assetId);
          onDebug?.(
            `工作台：素材 ${assetId} 已失效 — ${extractApiMessage(error) || 'NotFound'}`,
            true,
          );
        }
      }
    }),
  );

  const rows: MissingWorkspaceAsset[] = [];
  for (let i = 0; i < missingIds.length; i += 1) {
    const assetId = missingIds[i];
    const list = byAssetId[assetId] || [];
    for (let j = 0; j < list.length; j += 1) {
      const node = list[j];
      rows.push({ nodeId: node.id, name: node.name, assetId });
    }
  }
  return rows;
}
